/**
 * Hook: useEditProfileSectionStyles
 *
 * The shared inline styles of `EditUserProfilePage`'s settings-card sections
 * (`EditProfileBasicInfoSection`, `EditProfileSubscriptionSection`).
 *
 * These lived on the page while both sections were inlined there; extracting the
 * sections into their own files (Task 11 — the page crossed the 300-line ceiling)
 * left them with two consumers, so they get a single owner here rather than being
 * duplicated into each section. Purely presentational: no state, no logic.
 */

import type React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';

export interface EditProfileSectionStyles {
  /** Leading label of a form row ("Name", "Username", "Bio"). */
  labelStyle: React.CSSProperties;
  /** Small caption above a card ("Basic info", "Subscription"). */
  sectionHeaderStyle: React.CSSProperties;
  /** The rounded surface each section's fields sit on. */
  cardStyle: React.CSSProperties;
  /** A single field row inside a card. */
  rowStyle: React.CSSProperties;
  /** Hairline divider between rows. */
  separatorStyle: React.CSSProperties;
}

export function useEditProfileSectionStyles(): EditProfileSectionStyles {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return {
    labelStyle: {
      color: theme.text.secondary,
      fontSize: responsive.font.base,
      whiteSpace: 'nowrap',
    },
    sectionHeaderStyle: {
      color: theme.text.secondary,
      fontSize: responsive.font.sm,
      marginBottom: responsive.spacing.xs,
      paddingLeft: responsive.spacing.xs,
    },
    cardStyle: {
      backgroundColor: theme.surface,
      borderRadius: responsive.borderRadius.md,
      overflow: 'hidden',
    },
    rowStyle: {
      display: 'flex',
      alignItems: 'center',
      padding: responsive.spacing.base,
      gap: responsive.spacing.sm,
    },
    separatorStyle: {
      height: responsive.border.hairline,
      backgroundColor: theme.border,
      marginLeft: responsive.spacing.base,
      marginRight: responsive.spacing.base,
    },
  };
}
